// Helpers for fields that store rich text as HTML (e.g. Job comments). Values may be either
// legacy PLAIN TEXT (older records) or TipTap HTML (new records) — these render both safely.

const TAG_OK = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'UL', 'OL', 'LI', 'A', 'DIV', 'SPAN', 'IMG']);

// Bugify screenshots (owner 2026-09-20) and pictures in a portal document's Remark (owner
// 2026-09-28) are the images these fields may carry, and only as an
// upload of our own: an <img src> is a GET the browser makes by itself, so a foreign one is a
// tracking pixel and a data: URI is unbounded weight pasted into a row. Anything else loses its src
// below and is dropped.
// `blob:` is here for the EDITOR, and only the editor. A pasted screenshot is shown from an object
// URL of the file already in hand — instant, no round trip — and swapped for the canonical URL at
// save time. Without blob: in this list the sanitiser stripped the <img> the moment it was pasted,
// the re-seed effect then saw a difference and called setContent, and the picture vanished with no
// error anywhere (owner reported the broken-image icon; this was the second half of it).
//
// Safe, because a blob: URL is same-origin and made by this page — it can no more reach out than a
// string can. And it is not a hole in what gets STORED: the server's sanitiser
// (routes/bugify.ts) admits our own upload URLs alone, so a blob: that ever reached it would be
// dropped, not saved.
const OWN_IMAGE = /^(\/api\/(bugify\/images|portal\/doc-images)\/[A-Za-z0-9-]{8,64}|blob:[^"'\s]{1,200})$/;

// The editor emits colour as `style="color: #rrggbb"` and indent as `style="margin-left: Npx"`.
// Attributes are stripped wholesale below, so those two declarations are re-built from scratch
// after being parsed and bounds-checked — nothing from the input string is copied through.
const SAFE_COLOR = /^#[0-9a-f]{3,8}$|^rgba?\([\d.,\s%]+\)$/i;
function safeStyle(style: string): string {
  const out: string[] = [];
  for (const decl of String(style || '').split(';')) {
    const i = decl.indexOf(':'); if (i < 0) continue;
    const prop = decl.slice(0, i).trim().toLowerCase();
    const val = decl.slice(i + 1).trim();
    if (/url\(|expression|javascript:|@import/i.test(val)) continue;
    if (prop === 'color' && SAFE_COLOR.test(val)) out.push(`color: ${val}`);
    else if (prop === 'margin-left') {
      const px = parseFloat(val);
      if (Number.isFinite(px) && px > 0) out.push(`margin-left: ${Math.min(Math.round(px), 240)}px`);
    }
  }
  return out.join('; ');
}

export function looksLikeHtml(s: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(String(s || ''));
}

const escapeHtml = (s: string) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Whitelist sanitiser: keep only basic formatting tags, drop every attribute except a safe href
// on <a>. Defence-in-depth — TipTap already constrains its own output, but stored values are
// rendered with dangerouslySetInnerHTML, so we never trust them blindly.
export function sanitizeHtml(html: string): string {
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return escapeHtml(html);
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  const clean = (el: Element) => {
    for (const child of Array.from(el.children)) clean(child);   // post-order: children first
    if (el === doc.body) return;
    const tag = el.tagName;
    if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'IFRAME') { el.remove(); return; }
    if (!TAG_OK.has(tag)) {                                       // unwrap unknown tag, keep its (already-clean) children
      const parent = el.parentNode; if (!parent) return;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
      return;
    }
    if (tag === 'IMG' && !OWN_IMAGE.test((el.getAttribute('src') || '').trim())) { el.remove(); return; }
    const style = (tag === 'SPAN' || tag === 'P' || tag === 'LI' || tag === 'DIV') ? safeStyle(el.getAttribute('style') || '') : '';
    for (const attr of Array.from(el.attributes)) {
      if (tag === 'A' && attr.name === 'href' && /^(https?:|mailto:)/i.test(attr.value.trim())) continue;
      if (tag === 'IMG' && attr.name === 'src') continue;          // already checked against OWN_IMAGE
      el.removeAttribute(attr.name);
    }
    if (style) el.setAttribute('style', style);
    if (tag === 'A') { el.setAttribute('target', '_blank'); el.setAttribute('rel', 'noopener noreferrer'); }
  };
  clean(doc.body);
  return doc.body.innerHTML;
}

// Storage value → HTML ready to render (and to seed the editor). Plain text keeps its line breaks
// and auto-links bare URLs; HTML is sanitised.
export function commentToHtml(raw: string): string {
  const s = String(raw || '');
  if (!s.trim()) return '';
  if (looksLikeHtml(s)) return sanitizeHtml(s);
  const linked = escapeHtml(s).replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
  return linked.replace(/\r?\n/g, '<br>');
}

// Storage value → flat single-line text (for list previews, tooltips, search).
export function htmlToText(raw: string): string {
  const s = String(raw || '');
  if (!s) return '';
  if (!looksLikeHtml(s)) return s;
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return s.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const doc = new DOMParser().parseFromString(s, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}

// Storage value → plain text, one line per paragraph or bullet (for a hover that should read like the
// comment — htmlToText's single line runs the bullets together).
export function htmlToLines(raw: string): string {
  const s = String(raw || '');
  const lines = (x: string) => x.split(/\r?\n/).map(l => l.replace(/\s+/g, ' ').trim()).filter(Boolean).join('\n');
  if (!looksLikeHtml(s)) return lines(s);
  const marked = s.replace(/<li\b[^>]*>/gi, '\n• ').replace(/<br\s*\/?>|<\/(p|div|li)>/gi, '\n');
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return lines(marked.replace(/<[^>]+>/g, ' '));
  return lines(new DOMParser().parseFromString(marked, 'text/html').body.textContent || '');
}

// Bullet points added to the END of a stored comment (Active Jobs' plus button, report 49d227ca), in
// the format the value is already in — the rule issueDd follows for its Due Diligence link. HTML, and
// an empty value (new records are HTML), gets a bullet list; legacy plain text gets "• " lines, so its
// line breaks and bare links go on showing as they do now. Nothing already there is cut.
export function appendCommentBullets(raw: string, bullets: string[]): string {
  const s = String(raw || '');
  const list = `<ul>${bullets.map(b => `<li>${escapeHtml(b)}</li>`).join('')}</ul>`;
  if (looksLikeHtml(s) || !s.trim()) return s + list;
  // commentToHtml links a bare address through a double-quoted href, so a quote typed into one would
  // close that attribute early. Encoded the way a browser writes it in an address, it cannot.
  const safe = bullets.map(b => b.replace(/https?:\/\/\S+/g, u => u.replace(/"/g, '%22')));
  const text = `${s.replace(/\s+$/, '')}\n${safe.map(b => `• ${b}`).join('\n')}`;
  // Typed text that looks like a tag ("<client>") would tip the whole value into HTML and run the old
  // lines together; then the old part goes over as commentToHtml shows it today.
  return looksLikeHtml(text) ? commentToHtml(s) + list : text;
}

// ── A stored comment, one item at a time (Calem, report 49d227ca: "allow the user to edit or delete
// the comments inside the plus button pop-down") ─────────────────────────────────────────────────
// An item is what reads as one comment. In HTML: each bullet (<li>) with no list nested in it, and each
// top-level paragraph (<p>/<div>) with something in it, in the order they stand. In legacy plain text:
// each line that isn't blank. `text` is the item as one line — what its edit box starts from and what a
// later write is checked against — with a link's address in place of its words wherever the two
// differ, so an edit keeps the address (the Lease Reg share link is found by its address in the raw
// comments). `html` is the item ready to render; a bullet keeps its bullet, or its number.
//
// Whatever shows but is none of those — the words of a bullet that has sub-bullets, text set loose in
// front of a list — comes back `fixed`, where it stands, so the panel still shows the comment in full;
// it just gets no pencil or trash. Indexes count every entry, fixed ones too, and are the ones
// replaceCommentItem takes.
export type CommentItem = { text: string; html: string; fixed?: boolean };

const hasDom = () => typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined';
const filled = (n: Node): boolean => !!(n.textContent || '').trim()
  || (n.nodeType === 1 && ((n as Element).tagName === 'IMG' || !!(n as Element).querySelector('img')));

// Typed text → HTML: escaped, with bare addresses linked as commentToHtml links them. A quote in an
// address is encoded, so it can't end the href early.
const linkText = (s: string) => escapeHtml(s).replace(/https?:\/\/[^\s<]+/g,
  u => `<a href="${u.replace(/"/g, '&quot;')}" target="_blank" rel="noopener noreferrer">${u}</a>`);

// The comment in document order: an item (`el`), or a run of other nodes that show (`run`); `at` is the
// element they sit in.
type Piece = { el: Element | null; run: Node[]; at: Element };
function commentPieces(body: HTMLElement): Piece[] {
  const isItem = (n: Node): n is Element => {
    if (n.nodeType !== 1) return false;
    const el = n as Element;
    if (el.tagName === 'LI') return !el.querySelector('ul,ol') && filled(el);
    return (el.tagName === 'P' || el.tagName === 'DIV') && el.parentNode === body && !el.querySelector('ul,ol,li') && filled(el);
  };
  const out: Piece[] = [];
  const walk = (at: Element) => {
    let run: Node[] = [];
    const flush = () => { if (run.some(filled)) out.push({ el: null, run, at }); run = []; };
    for (const n of Array.from(at.childNodes)) {
      if (isItem(n)) { flush(); out.push({ el: n, run: [], at }); }
      else if (n.nodeType === 1 && Array.from((n as Element).querySelectorAll('li')).some(isItem)) { flush(); walk(n as Element); }
      else run.push(n);
    }
    flush();
  };
  walk(body);
  return out;
}

function pieceText(nodes: Node[]): string {
  let s = '';
  const walk = (n: Node) => {
    if (n.nodeType === 3) { s += n.nodeValue || ''; return; }
    if (n.nodeType !== 1) return;
    const el = n as Element;
    const href = el.tagName === 'A' ? (el.getAttribute('href') || '').trim() : '';
    if (/^https?:\/\//i.test(href) && (el.textContent || '').trim() !== href) { s += ` ${href} `; return; }
    const block = /^(BR|P|DIV|LI|UL|OL|BLOCKQUOTE|PRE)$/.test(el.tagName);   // a line break or a new block is a space
    if (block) s += ' ';
    for (const c of Array.from(el.childNodes)) walk(c);
    if (block) s += ' ';
  };
  nodes.forEach(walk);
  return s.replace(/\s+/g, ' ').trim();
}

function pieceHtml(p: Piece): string {
  const box = p.at.ownerDocument.createElement('div');
  const nodes: Node[] = p.el ? [p.el] : p.run;
  nodes.forEach(n => box.appendChild(n.cloneNode(true)));
  const li = p.el && p.el.tagName === 'LI' ? p.el : p.at.tagName === 'LI' ? p.at : null;
  if (!li) return sanitizeHtml(box.innerHTML);
  // A bullet on its own: its list is rebuilt around it, indented to its depth, numbered where it stood.
  const item = p.el ? sanitizeHtml(li.outerHTML) : `<li>${sanitizeHtml(box.innerHTML)}</li>`;
  let depth = 0;
  for (let a = li.parentElement; a; a = a.parentElement) if (a.tagName === 'UL' || a.tagName === 'OL') depth++;
  const style = `margin: 0 0 0 ${Math.max(0, depth - 1) * 20}px`;
  const list = li.parentElement;
  if (!list || list.tagName !== 'OL') return `<ul style="${style}">${item}</ul>`;
  const start = parseInt(list.getAttribute('start') || '', 10);
  const n = (Number.isFinite(start) ? start : 1) + Array.from(list.children).filter(c => c.tagName === 'LI').indexOf(li);
  return `<ol start="${n}" style="${style}">${item}</ol>`;
}

export function splitCommentItems(raw: string): CommentItem[] {
  const s = String(raw || '');
  if (!s.trim()) return [];
  if (!looksLikeHtml(s)) return s.split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(l => ({ text: l, html: commentToHtml(l) }));
  if (!hasDom()) return [{ text: htmlToText(s), html: commentToHtml(s), fixed: true }];
  const doc = new DOMParser().parseFromString(s, 'text/html');
  return commentPieces(doc.body).map(p => ({ text: pieceText(p.el ? [p.el] : p.run), html: pieceHtml(p), ...(p.el ? {} : { fixed: true }) }));
}

// The stored comment with item `index` (as splitCommentItems counts them) set to `newText`, or deleted
// when that is null or blank. HTML: the item's content becomes the typed text, escaped, with bare
// addresses linked; a deleted item takes with it a list it leaves empty (and a bullet that only held
// that list). Every other node is written back as it was. Plain text: the line is replaced or removed.
// An index that isn't an item leaves the value as it is.
export function replaceCommentItem(raw: string, index: number, newText: string | null): string {
  const s = String(raw || '');
  const text = String(newText ?? '').replace(/\s+/g, ' ').trim();
  if (!looksLikeHtml(s)) {
    const parts = s.split(/(\r?\n)/);   // the lines in the even places, the breaks between them in the odd
    let k = -1;
    for (let i = 0, n = -1; i < parts.length && k < 0; i += 2) if (parts[i].trim() && ++n === index) k = i;
    if (k < 0) return s;
    if (!text) parts.splice(k > 0 ? k - 1 : k, 2);   // the line and the break before it (after it, for the first)
    else parts[k] = text.replace(/https?:\/\/\S+/g, u => u.replace(/"/g, '%22'));   // as appendCommentBullets does
    const next = parts.join('');
    if (!next.trim()) return '';
    // Typed text that looks like a tag would tip the whole value into HTML and run its lines together;
    // then each line goes over as a paragraph of its own, shown as it is now, so no item moves.
    return looksLikeHtml(next) ? next.split(/\r?\n/).filter(l => l.trim()).map(l => `<p>${linkText(l.trim())}</p>`).join('') : next;
  }
  if (!hasDom()) return s;
  const doc = new DOMParser().parseFromString(s, 'text/html');
  const piece = commentPieces(doc.body)[index];
  if (!piece || !piece.el) return s;
  if (text) piece.el.innerHTML = linkText(text);
  else {
    let up = piece.el.parentElement;
    piece.el.remove();
    while (up && up !== doc.body && /^(UL|OL|LI)$/.test(up.tagName) && !filled(up)) { const next = up.parentElement; up.remove(); up = next; }
  }
  return filled(doc.body) ? doc.body.innerHTML : '';
}
